import React, { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronRight, faSearch } from "@fortawesome/free-solid-svg-icons";
import { Badge, Button, Card, Form, InputGroup, Modal, OverlayTrigger, Tooltip } from "react-bootstrap";
import { toast } from "react-toastify";
import { Loader, Message } from "../../components";
import { useAppContext } from "../../context/appContext";
import { UPDATE_TENANT_DISPLAY_FEATURES } from "../../graphql/mutations";
import { GET_TENANT_DISPLAY_FEATURES } from "../../graphql/queries";
import { isViewOnly } from "../../utils/permissions";
import {
  createCatalystFeatureHierarchy,
  createFeatureState,
  createFeatureToggleInput,
  featureDisplayPathKey,
  featurePathKey,
  getCatalystFeatureSupport,
  getChangedFeatureCount,
  getVisibleFeatureItems,
  toggleFeatureState,
  wouldDisableAllMainRouterFeatures
} from "../../utils/displayFeatures";
import "./FeatureConfigPage.css";

const MAIN_ROUTER_REQUIREMENT_MESSAGE = "At least one Main Routers feature must remain enabled.";

const confirmationCopy = {
  save: {
    title: "Save feature configuration",
    body: "This will replace this tenant's display feature configuration. The new feature visibility will apply when users re-login to the Catalyst app. Continue?",
    action: "Save configuration"
  },
  discard: {
    title: "Discard changes",
    body: "Your unsaved feature changes will be lost.",
    action: "Discard changes"
  },
  reset: {
    title: "Reset to defaults",
    body: "This will immediately submit the default state for every display feature.",
    action: "Reset to defaults"
  },
  navigate: {
    title: "Leave without saving?",
    body: "Your unsaved feature changes will be discarded if you continue.",
    action: "Discard and leave"
  },
  "navigate-save-tenant": {
    title: "Unsaved feature changes",
    body: "Saving tenant details will discard your unsaved feature toggle changes for this tenant. Continue?",
    action: "Discard and save tenant"
  }
};

const FeatureAssignmentSection = ({ env, tenantId, tenantName, viewOnly: viewOnlyProp = false }) => {
  const appContext = useAppContext() || {};
  const { user, registerNavigationGuard } = appContext;
  const viewOnly = viewOnlyProp || isViewOnly(user, env);
  const [draft, setDraft] = useState({});
  const [expanded, setExpanded] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [confirmation, setConfirmation] = useState(null);
  const [navigationRequest, setNavigationRequest] = useState(null);

  const { data, loading, error, refetch } = useQuery(GET_TENANT_DISPLAY_FEATURES, {
    variables: { tenantId },
    skip: !tenantId,
    fetchPolicy: "network-only"
  });
  const featuresPayload = data?.tenantDisplayFeatures;
  const sourceItems = useMemo(() => featuresPayload?.items || [], [featuresPayload]);
  const items = useMemo(() => createCatalystFeatureHierarchy(sourceItems), [sourceItems]);

  const initializedTenantRef = useRef(null);

  useEffect(() => {
    if (!featuresPayload || featuresPayload.tenantId !== tenantId) {
      return;
    }
    const displayItems = createCatalystFeatureHierarchy(featuresPayload.items);
    setDraft(createFeatureState(displayItems));

    // Only reset the search filter and expanded tree nodes when we switch to a
    // different tenant - not on every refetch of the same tenant (e.g. after save).
    if (initializedTenantRef.current !== tenantId) {
      setExpanded(
        displayItems.reduce((state, item) => {
          if (item.hasChildren && item.depth <= 1) {
            state[featureDisplayPathKey(item)] = true;
          }
          return state;
        }, {})
      );
      setSearchTerm("");
      initializedTenantRef.current = tenantId;
    }
  }, [featuresPayload, tenantId]);

  const [updateFeatures, { loading: saving }] = useMutation(UPDATE_TENANT_DISPLAY_FEATURES);
  const changedCount = getChangedFeatureCount(items, draft);
  // `items` can pick up freshly-loaded (or refetched) data before the effect above has synced
  // `draft` to match it - during that window, every enabled-by-default item would otherwise
  // count as "changed" against a draft that's still empty/stale, flipping isDirty true for an
  // instant and letting the navigation guard below race a Save click into a confirm dialog.
  // Gating on a populated draft (rather than the load timing) catches that window regardless of
  // how many renders it takes to resync, without ever masking a real user-made change - once a
  // real edit exists, draft already has as many entries as items from the initial sync.
  const isDirty = Object.keys(draft).length > 0 && changedCount > 0;
  const visibleItems = getVisibleFeatureItems(items, expanded, searchTerm);
  useEffect(() => {
    const warnBeforeUnload = (event) => {
      if (!isDirty) {
        return;
      }
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warnBeforeUnload);
    return () => window.removeEventListener("beforeunload", warnBeforeUnload);
  }, [isDirty]);

  useEffect(() => {
    if (!registerNavigationGuard) {
      return undefined;
    }
    return registerNavigationGuard((context) => {
      if (!isDirty) {
        return true;
      }
      return new Promise((resolve) => {
        setNavigationRequest({ resolve });
        setConfirmation(context === "save-tenant" ? "navigate-save-tenant" : "navigate");
      });
    });
  }, [isDirty, registerNavigationGuard]);

  const submitFeatureState = async (nextDraft, successMessage, resetToDefault = false) => {
    try {
      const result = await updateFeatures({
        variables: {
          tenantId,
          features: resetToDefault ? [] : createFeatureToggleInput(items, nextDraft),
          resetToDefault
        }
      });
      const updated = result.data?.updateTenantDisplayFeatures;
      if (updated) {
        setDraft(createFeatureState(createCatalystFeatureHierarchy(updated.items)));
      }
      toast.success(successMessage);
      await refetch();
    } catch (mutationError) {
      toast.error(
        mutationError?.message?.includes("At least one Main Routers feature")
          ? MAIN_ROUTER_REQUIREMENT_MESSAGE
          : "Could not update display features. Please try again."
      );
    }
  };

  const closeConfirmation = () => {
    navigationRequest?.resolve(false);
    setNavigationRequest(null);
    setConfirmation(null);
  };

  const confirmAction = async () => {
    const action = confirmation;
    setConfirmation(null);

    if (action === "save") {
      await submitFeatureState(draft, `Feature configuration saved for ${tenantName || tenantId}`);
      return;
    }
    if (action === "discard") {
      setDraft(createFeatureState(items));
      return;
    }
    if (action === "reset") {
      await submitFeatureState(createFeatureState(items, "defaultEnabled"), "Default feature configuration restored", true);
      return;
    }
    if (action === "navigate" || action === "navigate-save-tenant") {
      setDraft(createFeatureState(items));
      navigationRequest?.resolve(true);
      setNavigationRequest(null);
    }
  };

  return (
    <Card className="feature-config-page border mb-3">
      <Card.Body>
        <div className="mb-3">
          <h5 className="text-white p-2 mb-2" style={{ backgroundColor: "#243544" }}>
            Features Assignment
          </h5>
          <p className="text-muted mb-0">Control which product features are visible for this tenant.</p>
        </div>

        <Form.Group className="mb-3">
          <Form.Label htmlFor="feature-search" className="fw-semibold">
            Search features
          </Form.Label>
          <InputGroup>
            <InputGroup.Text>
              <FontAwesomeIcon icon={faSearch} />
            </InputGroup.Text>
            <Form.Control
              id="feature-search"
              type="search"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search by feature name or path"
              disabled={loading || Boolean(error)}
            />
          </InputGroup>
        </Form.Group>

        {error ? (
          <Message variant="danger">Could not load display features. Please try again.</Message>
        ) : loading && !items.length ? (
          <Loader />
        ) : (
          <div className="feature-tree" aria-label="Display features">
            <div className="feature-tree-header">
              <span>Feature</span>
              <span>Default state</span>
              <span className="text-end">Enabled</span>
            </div>
            {visibleItems.map((item, index) => {
              const key = featurePathKey(item.path);
              const displayKey = featureDisplayPathKey(item);
              const isExpanded = Boolean(expanded[displayKey]);
              const catalystSupport = getCatalystFeatureSupport(item, sourceItems);
              const mainRouterConstraint = wouldDisableAllMainRouterFeatures(items, draft, item);
              const tooltipMessage = mainRouterConstraint ? MAIN_ROUTER_REQUIREMENT_MESSAGE : catalystSupport.message;
              const toggle = (
                <Form.Check
                  type="switch"
                  className="d-inline-block"
                  id={`feature-toggle-${key}`}
                  aria-label={`Toggle ${item.name}`}
                  checked={Boolean(draft[key])}
                  onChange={() => setDraft((current) => toggleFeatureState(items, current, item))}
                  disabled={viewOnly || saving || mainRouterConstraint}
                />
              );

              return (
                <div className={`feature-tree-row catalyst-support-${catalystSupport.status}`} key={key}>
                  <div style={{ paddingLeft: `${Math.max(item.depth, 0) * 24}px` }}>
                    <div className="d-flex align-items-center">
                      {item.hasChildren ? (
                        <Button
                          variant="link"
                          className="feature-name-button p-0 me-2"
                          onClick={() => setExpanded((current) => ({ ...current, [displayKey]: !current[displayKey] }))}
                          aria-label={`${isExpanded ? "Collapse" : "Expand"} ${item.name}`}
                          aria-expanded={isExpanded}
                        >
                          <FontAwesomeIcon icon={isExpanded ? faChevronDown : faChevronRight} fixedWidth />
                        </Button>
                      ) : (
                        <span style={{ width: "22px" }} aria-hidden="true" />
                      )}
                      <div>
                        <div className="fw-semibold">{item.name}</div>
                        <div className="feature-path">{item.path.join(" / ")}</div>
                        {item.relationshipNote && <div className="feature-relationship-note">{item.relationshipNote}</div>}
                        {item.name === "Main Routers" && item.depth === 0 && (
                          <div className="feature-relationship-note">{MAIN_ROUTER_REQUIREMENT_MESSAGE}</div>
                        )}
                        {catalystSupport.message && (
                          <div className={`feature-support-note feature-support-note-${catalystSupport.status}`}>
                            {catalystSupport.message}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="feature-default-column">
                    <Badge bg={item.defaultEnabled ? "success" : "light"} text={item.defaultEnabled ? undefined : "dark"}>
                      {item.defaultEnabled ? "Enabled" : "Disabled"}
                    </Badge>
                  </div>
                  <div className="text-end">
                    {tooltipMessage ? (
                      <OverlayTrigger placement="left" overlay={<Tooltip id={`feature-toggle-tooltip-${index}`}>{tooltipMessage}</Tooltip>}>
                        <span className="feature-toggle-with-tooltip d-inline-block">{toggle}</span>
                      </OverlayTrigger>
                    ) : (
                      toggle
                    )}
                  </div>
                </div>
              );
            })}
            {!visibleItems.length && !loading && <div className="bg-white text-muted text-center py-5">No matching features found.</div>}
          </div>
        )}

        {!viewOnly && items.length > 0 && (
          <div className="d-flex justify-content-end mt-3">
            <Button
              variant="link"
              type="button"
              onClick={() => setConfirmation("reset")}
              disabled={saving || featuresPayload?.usesDefault}
              title={featuresPayload?.usesDefault ? "This tenant already uses the default configuration" : undefined}
            >
              Reset to defaults
            </Button>
          </div>
        )}

        {isDirty && !viewOnly && (
          <Card className="feature-save-bar mt-3">
            <Card.Body className="d-flex flex-wrap align-items-center justify-content-between gap-3 py-2">
              <div>
                <strong>
                  {changedCount} unsaved {changedCount === 1 ? "change" : "changes"}
                </strong>
              </div>
              <div className="d-flex gap-2">
                <Button type="button" variant="outline-secondary" onClick={() => setConfirmation("discard")} disabled={saving}>
                  Discard
                </Button>
                <Button type="button" variant="primary" onClick={() => setConfirmation("save")} disabled={saving}>
                  {saving ? "Saving..." : "Save changes"}
                </Button>
              </div>
            </Card.Body>
          </Card>
        )}
      </Card.Body>

      <Modal show={Boolean(confirmation)} onHide={closeConfirmation} centered>
        <Modal.Header closeButton>
          <Modal.Title>{confirmationCopy[confirmation]?.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{confirmationCopy[confirmation]?.body}</Modal.Body>
        <Modal.Footer>
          <Button type="button" variant="secondary" onClick={closeConfirmation}>
            Cancel
          </Button>
          <Button type="button" variant={confirmation === "reset" ? "danger" : "primary"} onClick={confirmAction} disabled={saving}>
            {saving ? "Saving..." : confirmation === "save" ? "Save configuration" : confirmationCopy[confirmation]?.action}
          </Button>
        </Modal.Footer>
      </Modal>
    </Card>
  );
};

export default FeatureAssignmentSection;
